import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { applyTypography, BaseButton, Chip } from '@repo/common';
import { ReactComponent as AccentIcon } from '@repo/common/assets/icons/16px/icon_16px_notice-accent.svg';

const STEP_DESCRIPTION = 'Explore workshops matched to this step.';

const CARD_CONFIG = [
  {
    id: 'step-01',
    stepKey: 'step01',
    path: '/community/kpathport/culture',
    chipText: 'step 01',
    bg: 'bg-grayscale-gray08',
    title: {
      text: 'Cultural Explorer',
      typoKey: null,
      className:
        'leading-[120%] text-grayscale-gray01 not-italic text-[32px] tabletH:text-[42px] desktopL:text-[60px]',
    },
    description: {
      text: STEP_DESCRIPTION,
      typoKey: 'b14',
      className: 'text-grayscale-gray04',
    },
  },
  {
    id: 'step-02',
    stepKey: 'step02',
    path: '/community/kpathport/career',
    chipText: 'step 02',
    bg: 'bg-grayscale-gray01',
    title: {
      text: 'Career Builder',
      typoKey: null,
      className:
        'text-[22px] leading-[120%] font-medium text-grayscale-gray07 tabletH:text-h5',
    },
    description: {
      text: STEP_DESCRIPTION,
      typoKey: 'b14',
      className: 'text-grayscale-gray07',
    },
  },
  {
    id: 'step-03',
    stepKey: 'step03',
    path: '/community/kpathport/etc',
    chipText: 'step 03',
    bg: 'bg-grayscale-gray01',
    title: {
      text: 'Koready Navigator',
      typoKey: null,
      className:
        'text-[22px] leading-[120%] font-medium text-grayscale-gray07 tabletH:text-h5',
    },
    description: {
      text: STEP_DESCRIPTION,
      typoKey: 'b14',
      className: 'text-grayscale-gray07',
    },
  },
  {
    id: 'step-04',
    stepKey: 'step04',
    path: '/community/kpathport/etc',
    chipText: 'step 04',
    bg: 'bg-grayscale-gray01',
    title: {
      text: 'Network Connector',
      typoKey: null,
      className:
        'text-[22px] leading-[120%] font-medium text-grayscale-gray07 tabletH:text-h5',
    },
    description: {
      text: STEP_DESCRIPTION,
      typoKey: 'b14',
      className: 'text-grayscale-gray07',
    },
  },
  {
    id: 'step-05',
    stepKey: 'step05',
    path: '/community/kpathport/etc',
    chipText: 'step 05',
    bg: 'bg-green-light03',
    title: {
      text: 'Be Koready',
      typoKey: null,
      className:
        'text-[22px] leading-[120%] font-medium text-grayscale-gray07 tabletH:text-h5',
    },
    description: {
      text: STEP_DESCRIPTION,
      typoKey: 'b14',
      className: 'text-grayscale-gray07',
    },
  },
];

const buildTextClassName = ({ typoKey, text, lang, className }) => {
  const baseClass = typoKey ? applyTypography(typoKey, text, lang) : '';
  return [baseClass, className].filter(Boolean).join(' ');
};

const getOngoingStepKey = () => {
  if (typeof window === 'undefined') {
    return 'step01';
  }

  const ongoingStep = CARD_CONFIG.find(
    ({ stepKey }) =>
      window.localStorage.getItem(`${stepKey}_booking_status`) === 'ongoing',
  );

  return ongoingStep?.stepKey || 'step01';
};

const StepCard = ({
  item,
  isOngoing = false,
  className = '',
  titleGapClassName = '',
}) => {
  const navigate = useNavigate();
  const { id, path, chipText, bg, title, description } = item;

  return (
    <BaseButton
      type='text'
      size='m'
      htmlType='button'
      hideIcon
      typo={false}
      isStatic
      onClick={() => navigate(path)}
      textWrapperClass='flex h-full w-full flex-col justify-between'
      className={`h-auto min-w-0 p-0 rounded-none flex-col justify-between border-0 text-left [&>span]:h-full [&>span]:flex-1 [&>span]:items-stretch ${bg} ${className} cursor-pointer transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-deep03`}
    >
      <div className='flex flex-col items-end w-full'>
        <Chip
          type='fill'
          state='disabled'
          icon={isOngoing ? <AccentIcon /> : undefined}
          hideIcon={!isOngoing}
          style={{
            backgroundColor: 'var(--surface-black40)',
            color: 'var(--grayscale-gray00)',
            gap: isOngoing ? 0 : undefined,
          }}
          borderclass='ring-0'
        >
          {chipText}
        </Chip>
      </div>

      <div
        className={`flex flex-col items-start justify-end w-full not-italic whitespace-pre-wrap ${titleGapClassName}`}
      >
        {title?.text && (
          <span
            style={id === 'step-01' ? { fontFamily: 'High Summit' } : undefined}
            className={buildTextClassName(title)}
          >
            {title.text}
          </span>
        )}
        {description?.text && (
          <span className={buildTextClassName(description)}>
            {description.text}
          </span>
        )}
      </div>
    </BaseButton>
  );
};

const Card = () => {
  const [ongoingStepKey, setOngoingStepKey] = useState(getOngoingStepKey);
  const cardItems = useMemo(
    () =>
      CARD_CONFIG.map((item) => ({
        ...item,
        isOngoing: item.stepKey === ongoingStepKey,
      })),
    [ongoingStepKey],
  );

  useEffect(() => {
    const syncOngoingStep = () => {
      setOngoingStepKey(getOngoingStepKey());
    };

    window.addEventListener('storage', syncOngoingStep);
    window.addEventListener('focus', syncOngoingStep);

    return () => {
      window.removeEventListener('storage', syncOngoingStep);
      window.removeEventListener('focus', syncOngoingStep);
    };
  }, []);

  return (
    <div className='flex flex-col w-full h-full gap-3 tabletH:gap-4'>
      <div className='flex flex-col items-center w-full gap-3 tabletH:hidden'>
        {cardItems.map((item) => (
          <StepCard
            key={item.id}
            item={item}
            isOngoing={item.isOngoing}
            className={`rounded-[30px] overflow-clip w-full max-w-[540px] min-w-[240px] p-6 ${
              item.id === 'step-01' ? 'h-[260px]' : 'h-[160px]'
            }`}
            titleGapClassName='gap-1'
          />
        ))}
      </div>

      <div className='hidden tabletH:flex tabletH:flex-col tabletH:w-full tabletH:gap-4'>
        <div className='flex flex-wrap items-center content-center w-full gap-4'>
          {cardItems.slice(0, 2).map((item) => (
            <StepCard
              key={item.id}
              item={item}
              isOngoing={item.isOngoing}
              className={`rounded-[42px] h-[280px] overflow-clip tabletH:flex-[1_0_0] tabletH:min-h-[280px] tabletH:pl-10 tabletH:pr-[30px] tabletH:pt-[30px] tabletH:pb-10 ${
                item.id === 'step-01'
                  ? 'tabletH:min-w-[380px]'
                  : 'tabletH:min-w-[240px]'
              } desktopL:min-w-[240px] desktopL:h-[280px] desktopL:pl-10 desktopL:pr-[30px] desktopL:pt-[30px] desktopL:pb-10 desktopL:rounded-[42px]`}
              titleGapClassName={
                item.id === 'step-01'
                  ? 'tabletH:gap-2 desktopL:gap-2.5'
                  : 'tabletH:gap-[10px] desktopL:gap-2.5'
              }
            />
          ))}
        </div>

        <div className='flex flex-wrap items-start content-start w-full gap-4'>
          {cardItems.slice(2).map((item) => (
            <StepCard
              key={item.id}
              item={item}
              isOngoing={item.isOngoing}
              className='rounded-[42px] h-[280px] overflow-clip tabletH:flex-[1_0_0] tabletH:min-h-[280px] tabletH:min-w-[240px] tabletH:pl-10 tabletH:pr-[30px] tabletH:pt-[30px] tabletH:pb-10 desktopL:min-w-[240px] desktopL:h-[280px] desktopL:pl-10 desktopL:pr-[30px] desktopL:pt-[30px] desktopL:pb-10 desktopL:rounded-[42px]'
              titleGapClassName='tabletH:gap-[10px] desktopL:gap-2.5'
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Card;
